// ─────────────────────────────────────────────────────────────────────────────
// pages/StudyMaterials/parts/MaterialDetailsModal.tsx
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import Hls from 'hls.js';

// ── Types ─────────────────────────────────────────────────────────────────────

interface StudyMaterialFile {
  file_name: string;
  key: string;
  url: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
}

interface HLSVideo {
  video_id: string;
  original_name: string;
  m3u8_url: string | null;
  size: number;
  status: string;
}

interface StudyMaterial {
  id: string;
  title: string;
  body: string;
  files: StudyMaterialFile[];
  video_links: string[];
  videos: HLSVideo[];
  created_at: string;
}

interface MaterialDetailsModalProps {
  visible: boolean;
  material: StudyMaterial;
  onHide: () => void;
  onEdit?: () => void;
}

// ── Main component ────────────────────────────────────────────────────────────

const MaterialDetailsModal: React.FC<MaterialDetailsModalProps> = ({
  visible,
  material,
  onHide,
  onEdit,
}) => {
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) setActiveVideoId(null);
  }, [visible]);

  const readyVideos      = material.videos?.filter((v) => v.status === 'ready')      ?? [];
  const processingVideos = material.videos?.filter((v) => v.status === 'processing') ?? [];

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      style={{ width: '92vw', maxWidth: '860px' }}
      dismissableMask
      draggable={false}
      pt={{
        root:    { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: 0 } },
      }}
    >
      <div style={{ background: '#f8f9fa', minHeight: '300px' }}>

        {/* ── Header ── */}
        <div style={{
          background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
          padding: '28px 28px 24px',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: '-40px', right: '-40px',
            width: '140px', height: '140px',
            background: 'rgba(212,241,126,0.1)',
            borderRadius: '50%', filter: 'blur(40px)',
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'inline-block', padding: '4px 10px',
                  background: 'rgba(212,241,126,0.2)', borderRadius: '16px',
                  marginBottom: '8px', fontSize: '0.65rem', fontWeight: '600',
                  letterSpacing: '0.5px', textTransform: 'uppercase',
                }}>
                  Study Material
                </div>
                <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700' }}>
                  {material.title}
                </h2>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem' }}>
                  <i className="pi pi-calendar" style={{ marginRight: '6px' }} />
                  {formatDate(material.created_at)}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {material.files.length > 0 && (
                  <StatPill icon="pi-paperclip" label={`${material.files.length} file${material.files.length !== 1 ? 's' : ''}`} />
                )}
                {readyVideos.length > 0 && (
                  <StatPill icon="pi-play-circle" label={`${readyVideos.length} video${readyVideos.length !== 1 ? 's' : ''}`} green />
                )}
                {processingVideos.length > 0 && (
                  <StatPill icon="pi-spinner pi-spin" label={`${processingVideos.length} processing`} orange />
                )}
                {material.video_links.length > 0 && (
                  <StatPill icon="pi-external-link" label={`${material.video_links.length} link${material.video_links.length !== 1 ? 's' : ''}`} />
                )}
              </div>
            </div>
          </div>

          <button
            onClick={onHide}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'rgba(255,255,255,0.15)', border: 'none',
              borderRadius: '8px', color: 'white', width: '32px', height: '32px',
              cursor: 'pointer', fontSize: '1rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <i className="pi pi-times" />
          </button>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {material.body && (
            <Section title="Description" icon="pi-align-left">
              <div style={{ fontSize: '0.92rem', color: '#333', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                {material.body}
              </div>
            </Section>
          )}

          {/* ── HLS Videos ── */}
          {material.videos && material.videos.length > 0 && (
            <Section title={`Videos (${material.videos.length})`} icon="pi-video">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {material.videos.map((video) => (
                  <VideoCard
                    key={video.video_id}
                    video={video}
                    isActive={activeVideoId === video.video_id}
                    onPlay={() => setActiveVideoId(activeVideoId === video.video_id ? null : video.video_id)}
                  />
                ))}
              </div>
            </Section>
          )}

          {/* ── Files ── */}
          {material.files.length > 0 && (
            <Section title={`Attachments (${material.files.length})`} icon="pi-paperclip">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {material.files.map((file, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px', background: 'white',
                      border: '1px solid #e0e0e0', borderRadius: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                      <i className={getFileIcon(file.mime_type)} style={{ fontSize: '1.4rem', color: '#0f3d3e' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.file_name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                          {formatFileSize(file.size)}
                        </div>
                      </div>
                    </div>
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '6px 12px', background: '#0f3d3e', color: 'white',
                        textDecoration: 'none', borderRadius: '6px',
                        fontSize: '0.8rem', fontWeight: '600', flexShrink: 0,
                      }}
                    >
                      <i className="pi pi-download" style={{ marginRight: '4px' }} />
                      Download
                    </a>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── Video Links ── */}
          {material.video_links.length > 0 && (
            <Section title={`Video Links (${material.video_links.length})`} icon="pi-external-link">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {material.video_links.map((link, idx) => (
                  <a
                    key={idx}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '12px', background: 'white',
                      border: '1px solid #e0e0e0', borderRadius: '8px',
                      color: '#0f3d3e', textDecoration: 'none',
                      fontSize: '0.85rem', display: 'flex', alignItems: 'center',
                      gap: '8px', transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f5f5f5';
                      e.currentTarget.style.borderColor = '#0f3d3e';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <i className="pi pi-external-link" style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {link}
                    </span>
                  </a>
                ))}
              </div>
            </Section>
          )}

          {/* ── Footer ── */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '4px' }}>
            {onEdit && (
              <GlobalButton label="Edit Material" variant="primary" icon="pi pi-pencil" onClick={onEdit} />
            )}
            <GlobalButton label="Close" variant="outline" onClick={onHide} />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// VideoCard
// Core fix: <video> is ALWAYS in the DOM (hidden via CSS, never conditionally
// unmounted). This means videoRef.current is always valid when useEffect runs,
// eliminating the race condition that caused the frozen 00:00 bug.
// ─────────────────────────────────────────────────────────────────────────────

interface VideoCardProps {
  video: HLSVideo;
  isActive: boolean;
  onPlay: () => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ video, isActive, onPlay }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef   = useRef<Hls | null>(null);

  const isReady      = video.status === 'ready' && !!video.m3u8_url;
  const isProcessing = video.status === 'processing';
  const isFailed     = video.status === 'failed';

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !isReady) return;

    if (isActive) {
      // Destroy any stale hls instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      // Native HLS (Safari)
      if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
        videoEl.src = video.m3u8_url!;
        videoEl.load();
        videoEl.play().catch(() => {});
        return;
      }

      // hls.js (Chrome / Firefox)
      if (!Hls.isSupported()) {
        console.error('[HLS] Not supported in this browser');
        return;
      }

      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
      });

      hls.loadSource(video.m3u8_url!);
      hls.attachMedia(videoEl);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoEl.play().catch(() => {});
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              break;
          }
        }
      });

      hlsRef.current = hls;

    } else {
      // Stop & reset when closed
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      videoEl.pause();
      videoEl.src = '';
      videoEl.load();
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [isActive, isReady, video.m3u8_url]);

  return (
    <div style={{
      border: `2px solid ${isActive ? '#0f3d3e' : '#e0e0e0'}`,
      borderRadius: '12px',
      overflow: 'hidden',
      background: 'white',
      transition: 'border-color 0.2s ease',
    }}>
      {/* Info row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px' }}>

        <div style={{
          width: '42px', height: '42px', borderRadius: '10px', flexShrink: 0,
          background: isReady ? '#dcfce7' : isProcessing ? '#fef3c7' : '#fee2e2',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isReady      && <i className="pi pi-play-circle"     style={{ fontSize: '1.3rem', color: '#16a34a' }} />}
          {isProcessing && <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.1rem', color: '#d97706' }} />}
          {isFailed     && <i className="pi pi-times-circle"    style={{ fontSize: '1.3rem', color: '#dc2626' }} />}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '0.88rem', fontWeight: '600', color: '#1a1a1a',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {video.original_name}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '2px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span>{formatVideoSize(video.size)}</span>
            {isProcessing && <span style={{ color: '#d97706', fontWeight: '600' }}>Processing...</span>}
            {isFailed     && <span style={{ color: '#dc2626', fontWeight: '600' }}>Failed</span>}
            {isReady      && <span style={{ color: '#16a34a', fontWeight: '600' }}>Ready</span>}
          </div>
        </div>

        {isReady && (
          <button
            onClick={onPlay}
            style={{
              padding: '8px 16px',
              background: isActive ? '#0f3d3e' : 'transparent',
              border: '2px solid #0f3d3e',
              borderRadius: '8px',
              color: isActive ? 'white' : '#0f3d3e',
              fontWeight: '600',
              fontSize: '0.8rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease',
              flexShrink: 0,
            }}
          >
            <i className={`pi ${isActive ? 'pi-times' : 'pi-play'}`} />
            {isActive ? 'Close' : 'Play'}
          </button>
        )}

        {isProcessing && (
          <span style={{
            fontSize: '0.75rem', color: '#d97706', background: '#fef3c7',
            padding: '4px 10px', borderRadius: '6px', fontWeight: '600', flexShrink: 0,
          }}>
            Processing
          </span>
        )}
      </div>

      {/*
        Always render <video> — show/hide with CSS only.
        This keeps videoRef.current always valid so hls.js
        can attach immediately without any timing issues.
      */}
      <div style={{
        background: '#000',
        borderTop: isActive ? '2px solid #0f3d3e' : 'none',
        display: isActive ? 'block' : 'none',
      }}>
        <video
          ref={videoRef}
          controls
          playsInline
          preload="none"
          style={{ width: '100%', maxHeight: '480px', display: 'block' }}
        />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper sub-components
// ─────────────────────────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e9ecef' }}>
    <h3 style={{ margin: '0 0 16px 0', fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e', display: 'flex', alignItems: 'center', gap: '8px' }}>
      <i className={`pi ${icon}`} />
      {title}
    </h3>
    {children}
  </div>
);

const StatPill: React.FC<{ icon: string; label: string; green?: boolean; orange?: boolean }> = ({ icon, label, green, orange }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '5px',
    background: green ? 'rgba(22,163,74,0.15)' : orange ? 'rgba(217,119,6,0.15)' : 'rgba(255,255,255,0.15)',
    borderRadius: '12px', padding: '4px 10px', fontSize: '0.75rem', fontWeight: '600',
    color: green ? '#86efac' : orange ? '#fcd34d' : 'rgba(255,255,255,0.85)',
  }}>
    <i className={`pi ${icon}`} style={{ fontSize: '0.7rem' }} />
    {label}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Utility helpers
// ─────────────────────────────────────────────────────────────────────────────

const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const formatVideoSize = (bytes: number): string => {
  if (bytes < 1048576)    return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
  return (bytes / 1073741824).toFixed(2) + ' GB';
};

const getFileIcon = (mimeType: string): string => {
  if (mimeType.includes('pdf'))                                        return 'pi pi-file-pdf';
  if (mimeType.includes('word'))                                       return 'pi pi-file-word';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'pi pi-file-excel';
  if (mimeType.includes('image'))                                      return 'pi pi-image';
  return 'pi pi-file';
};

export default MaterialDetailsModal;